/**
 * Firebase ID-token retrieval for the copilot transports.
 *
 * The copilot server (when auth is enabled) requires a Firebase ID
 * token on:
 *   - the WebRTC `/api/offer` POST (Authorization: Bearer ...)
 *   - the WS `session.init` message (auth_token field)
 *
 * Both call `getCopilotAuthToken()` immediately before issuing the
 * handshake, so each new connection / reconnect gets a fresh-enough
 * token. Firebase auto-refreshes the cached ID token when it's within
 * 5 minutes of expiry, so we don't force a refresh per call — that
 * would be a needless round-trip to Firebase on every reconnect.
 *
 * The `localStorage` fallback exists because the axios interceptor
 * at `adapters/http.js` is the authoritative writer of `jwtToken`,
 * and during the brief window between Firebase init and the
 * interceptor refresh it's possible for `auth.currentUser` to be
 * available before `getIdToken()` resolves. The fallback keeps the
 * copilot working through that window.
 */

import { auth } from "firebase-setup";

export async function getCopilotAuthToken() {
  try {
    const user = auth?.currentUser;
    if (user) {
      const token = await user.getIdToken(false);
      if (typeof token === "string" && token.length > 0) return token;
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[copilot] getIdToken failed", {
      error: err?.message ?? String(err),
    });
  }
  try {
    const cached = localStorage.getItem("jwtToken");
    if (typeof cached === "string" && cached.length > 0) return cached;
  } catch {
    // localStorage can throw in private-mode Safari, etc.
  }
  return null;
}
